package com.sddamico.draftmachine.pages.pools

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.components.layouts.LoadingFrame
import com.sddamico.draftmachine.components.layouts.LoadingSection
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.api.apiDelete
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.sections.AppHeader
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Avatar
import com.sddamico.draftmachine.ds.core.AvatarSize
import com.sddamico.draftmachine.ds.core.Badge
import com.sddamico.draftmachine.ds.core.BadgeTone
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.feedback.EmptyState
import com.sddamico.draftmachine.ds.feedback.Toast
import com.sddamico.draftmachine.ds.feedback.ToastTone
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.forms.Select
import com.sddamico.draftmachine.ds.forms.Switch
import com.sddamico.draftmachine.ds.forms.Textarea
import com.sddamico.draftmachine.models.AddPlayerToPoolRequest
import com.sddamico.draftmachine.models.ClonePoolRequest
import com.sddamico.draftmachine.models.NewPlayerEntry
import com.sddamico.draftmachine.models.PlayerPoolDetailResponse
import com.sddamico.draftmachine.models.PlayerPoolResponse
import com.sddamico.draftmachine.models.PlayerResponse
import com.sddamico.draftmachine.models.UpdatePoolRequest
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.rememberAuthState
import com.sddamico.draftmachine.util.sportLabel
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Text

private const val PLAYERS_SHOWN = 60

/**
 * Pool detail (frames 1c–1d): badges + forged-by, big nameplate, use/clone actions, included
 * pools, and a searchable direct-player list. Owners get an edit mode (rename, description,
 * public switch, add/remove players and child pools).
 */
@Page("{id}")
@Composable
fun PoolDetailPage() {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()
  val poolId = ctx.route.params["id"].orEmpty()

  when (val s = auth.value) {
    is LoadState.Loading -> LoadingFrame()
    is LoadState.Failed -> { LaunchedEffect(Unit) { ctx.router.transitionTo("/login") } }
    is LoadState.Ready -> {
      if (s.value == null) {
        LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      } else {
        PoolDetailBody(email = s.value!!.email, poolId = poolId)
      }
    }
  }
}

@Composable
private fun PoolDetailBody(email: String, poolId: String) {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()
  var detail by remember { mutableStateOf<LoadState<PlayerPoolDetailResponse>>(LoadState.Loading) }
  var reloadKey by remember { mutableStateOf(0) }
  var editing by remember { mutableStateOf(false) }
  var busy by remember { mutableStateOf(false) }
  var errorFlash by remember { mutableStateOf<String?>(null) }

  suspend fun fetch() {
    val loaded = try {
      LoadState.Ready(apiGet("/api/player-pool-detail?poolId=$poolId", PlayerPoolDetailResponse.serializer()))
    } catch (e: ApiException) {
      LoadState.Failed(e.errorMessage)
    } catch (e: Throwable) {
      LoadState.Failed(UNREACHABLE_MESSAGE)
    }
    ViewTransitions.run(Motion.Update) { detail = loaded }
  }

  LaunchedEffect(poolId, reloadKey) { fetch() }

  fun refresh() { reloadKey++ }

  fun runAction(block: suspend () -> Unit) {
    if (busy) return
    busy = true
    errorFlash = null
    scope.launch {
      try {
        block()
        refresh()
      } catch (e: ApiException) {
        errorFlash = e.errorMessage
      } catch (e: Throwable) {
        errorFlash = UNREACHABLE_MESSAGE
      }
      busy = false
    }
  }

  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = {
      AppHeader(
        email = email, title = "Player pools",
        onBack = { ctx.router.transitionTo("/pools") },
      )
    },
  ) {
    when (val d = detail) {
      is LoadState.Loading -> LoadingSection(padding = 60.px)
      is LoadState.Failed -> EmptyState(icon = "plugs", title = "Couldn't open this pool", message = d.message)
      is LoadState.Ready -> {
        if (errorFlash != null) {
          Div(attrs = Modifier.padding(top = 12.px, leftRight = 16.px).toAttrs()) {
            Toast(title = errorFlash, tone = ToastTone.Rust, onClose = { errorFlash = null })
          }
        }
        if (editing) {
          PoolEditor(
            detail = d.value, busy = busy,
            onDone = { editing = false; refresh() },
            onAction = ::runAction,
          )
        } else {
          PoolView(
            detail = d.value, busy = busy,
            onUse = { ctx.router.transitionTo("/draft/new?poolId=$poolId") },
            onClone = {
              runAction {
                val cloned = apiPost(
                  "/api/player-pool-clone",
                  apiJson.encodeToString(ClonePoolRequest.serializer(), ClonePoolRequest(poolId)),
                  PlayerPoolResponse.serializer(),
                )
                ctx.router.transitionTo("/pools/${cloned.id}")
              }
            },
            onEdit = { editing = true },
            onOpenChild = { ctx.router.transitionTo("/pools/${it.id}") },
          )
        }
      }
    }
  }
}

// ── Read view ────────────────────────────────────────────────────────────────

@Composable
private fun PoolView(
  detail: PlayerPoolDetailResponse,
  busy: Boolean,
  onUse: () -> Unit,
  onClone: () -> Unit,
  onEdit: () -> Unit,
  onOpenChild: (PlayerPoolResponse) -> Unit,
) {
  val pool = detail.pool
  var search by remember { mutableStateOf("") }

  Div(
    attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(12.px)
      .padding(top = 20.px, leftRight = 16.px, bottom = 24.px).toAttrs()
  ) {
    Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(8.px).toAttrs()) {
      when {
        pool.isOfficial -> Badge("Official", tone = BadgeTone.Brass, icon = "seal-check")
        pool.isPublic -> Badge("Public", tone = BadgeTone.Glass, icon = "globe-hemisphere-west")
        else -> Badge("Private", tone = BadgeTone.Neutral, icon = "lock-simple")
      }
      if (pool.isPublic && !pool.isOfficial && pool.creatorUsername != null) {
        Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(6.px).toAttrs()) {
          Avatar(name = pool.creatorUsername!!, size = AvatarSize.Xs)
          SpanText(
            "FORGED BY @${pool.creatorUsername!!}",
            Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textSecondary),
          )
        }
      }
    }
    SpanText(
      pool.name,
      Modifier.fontFamily(*DmType.display).fontSize(26.px).lineHeight(1.05).color(DmColor.text),
    )
    SpanText(
      detailMeta(detail),
      Modifier.fontFamily(*DmType.mono).fontSize(11.px)
        .color(if (detail.children.isNotEmpty()) DmColor.glass300 else DmColor.textMuted),
    )
    pool.description?.takeIf { it.isNotBlank() }?.let {
      SpanText(
        it,
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textSecondary).lineHeight(1.55),
      )
    }
    Div(attrs = Modifier.displayFlex().gap(10.px).toAttrs()) {
      Button("Use in a draft", variant = ButtonVariant.Primary, icon = "lightning", onClick = onUse)
      Button(
        if (detail.canEdit) "Edit" else "Clone",
        variant = ButtonVariant.Ghost,
        icon = if (detail.canEdit) "pencil-simple" else "copy",
        disabled = busy,
        onClick = if (detail.canEdit) onEdit else onClone,
      )
    }

    if (detail.children.isNotEmpty()) {
      SpanText("Included pools", Modifier.dmStamp().margin(top = 4.px))
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
        detail.children.forEach { child -> ChildPoolRow(child, onClick = { onOpenChild(child) }) }
      }
    }

    val direct = detail.directPlayers
    if (detail.children.isNotEmpty()) {
      SpanText("Direct players · ${formatCount(direct.size)}", Modifier.dmStamp().margin(top = 4.px))
    }
    if (direct.isNotEmpty()) {
      Input(
        placeholder = "Search ${formatCount(direct.size)} players",
        icon = "magnifying-glass",
        value = search,
        onValueChange = { search = it },
      )
      PlayerRows(direct, search)
    } else if (detail.children.isEmpty()) {
      EmptyState(icon = "user", title = "No players yet", message = "This pool is still empty.")
    }
  }
}

@Composable
private fun PlayerRows(players: List<PlayerResponse>, search: String, onRemove: ((PlayerResponse) -> Unit)? = null) {
  val q = search.trim().lowercase()
  val filtered = if (q.isEmpty()) players else players.filter { it.name.lowercase().contains(q) }
  val shown = filtered.take(PLAYERS_SHOWN)

  Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
    shown.forEach { p ->
      PoolPlayerRow(p.sport, p.name, playerMeta(p), onRemove = onRemove?.let { { it(p) } })
    }
  }
  if (filtered.size > shown.size) {
    Div(attrs = Modifier.displayFlex().justifyContent(JustifyContent.Center).padding(top = 4.px).toAttrs()) {
      SpanText(
        "Showing ${shown.size} of ${formatCount(filtered.size)}",
        Modifier.dmStamp().color(DmColor.steel500),
      )
    }
  }
}

private fun detailMeta(detail: PlayerPoolDetailResponse): String {
  val pool = detail.pool
  return if (detail.children.isNotEmpty()) {
    val pools = "${detail.children.size} POOL${if (detail.children.size == 1) "" else "S"}"
    "$pools + ${formatCount(detail.directPlayers.size)} DIRECT PLAYERS · ${formatCount(detail.totalPlayerCount)} TOTAL"
  } else {
    "${pool.sport?.let(::sportLabel) ?: "MIXED"} · ${formatCount(detail.totalPlayerCount)} PLAYERS"
  }
}

// ── Edit mode ────────────────────────────────────────────────────────────────

@Composable
private fun PoolEditor(
  detail: PlayerPoolDetailResponse,
  busy: Boolean,
  onDone: () -> Unit,
  onAction: (suspend () -> Unit) -> Unit,
) {
  val pool = detail.pool
  var name by remember { mutableStateOf(pool.name) }
  var description by remember { mutableStateOf(pool.description ?: "") }
  var isPublic by remember { mutableStateOf(pool.isPublic) }
  var playerEntry by remember { mutableStateOf("") }
  var entryKey by remember { mutableStateOf(0) }
  var entrySport by remember { mutableStateOf(pool.sport) }
  var search by remember { mutableStateOf("") }
  val nameError = if (name.isBlank()) "Pool name is required" else null

  Div(
    attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(14.px)
      .padding(top = 20.px, leftRight = 16.px, bottom = 24.px).toAttrs()
  ) {
    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.SpaceBetween).gap(12.px).toAttrs()
    ) {
      SpanText("Edit pool", Modifier.fontFamily(*DmType.display).fontSize(24.px).color(DmColor.text))
      Button("Done", variant = ButtonVariant.Secondary, icon = "check", disabled = busy, onClick = onDone)
    }

    Div(attrs = Modifier.poolPanel().toAttrs()) {
      SpanText("Nameplate", Modifier.dmStamp())
      Input(
        label = "Pool name", icon = "tag", value = name, error = nameError,
        onValueChange = { name = it },
      )
      Textarea(
        label = "Description · optional", placeholder = "What belongs in here, and why?",
        value = description, rows = 2, onValueChange = { description = it },
      )
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
          .justifyContent(JustifyContent.SpaceBetween).gap(12.px).toAttrs()
      ) {
        Div {
          SpanText(
            "List it for the community",
            Modifier.roleH3().fontSize(15.px).displayBlock(),
          )
          SpanText(
            "Anyone can use or clone it.",
            Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted).displayBlock(),
          )
        }
        Switch(checked = isPublic, onChange = { isPublic = it })
      }
      Button(
        "Save Pool", variant = ButtonVariant.Primary, icon = "check",
        disabled = busy || nameError != null,
        onClick = {
          onAction {
            apiPost(
              "/api/player-pool-update",
              apiJson.encodeToString(
                UpdatePoolRequest.serializer(),
                UpdatePoolRequest(pool.id, name.trim(), description.trim().ifBlank { null }, pool.sport, isPublic),
              ),
              PlayerPoolResponse.serializer(),
            )
          }
        },
      )
    }

    Div(attrs = Modifier.poolPanel().toAttrs()) {
      SpanText("Add players", Modifier.dmStamp())
      if (pool.sport == null) {
        Select(
          label = "Sport for new players",
          value = entrySport?.name ?: "",
          onValueChange = { entrySport = runCatching { Sport.valueOf(it) }.getOrNull() },
        ) {
          Option(value = "") { Text("Pick a sport") }
          Sport.entries.forEach { sp -> Option(value = sp.name) { Text(sportLabel(sp)) } }
        }
      }
      Div(attrs = Modifier.displayFlex().gap(8.px).toAttrs()) {
        androidx.compose.runtime.key(entryKey) {
          Input(
            placeholder = playerEntryPlaceholder(pool.sport ?: entrySport), icon = "user",
            value = playerEntry, onValueChange = { playerEntry = it },
            modifier = Modifier.flex(1),
            onEnter = {},
          )
        }
        IconButton(
          icon = "plus", label = "Add player", variant = ButtonVariant.Secondary,
          disabled = busy || playerEntry.isBlank() || (pool.sport == null && entrySport == null),
          onClick = {
            val entry = parsePlayerEntry(playerEntry, pool.sport ?: entrySport)
            if (entry != null) {
              onAction {
                apiPost(
                  "/api/pool-players",
                  apiJson.encodeToString(
                    AddPlayerToPoolRequest.serializer(),
                    AddPlayerToPoolRequest(pool.id, player = entry),
                  ),
                  SuccessResponse.serializer(),
                )
                playerEntry = ""
                entryKey++
              }
            }
          },
        )
      }
      val direct = detail.directPlayers
      if (direct.isNotEmpty()) {
        Input(
          placeholder = "Search ${formatCount(direct.size)} players",
          icon = "magnifying-glass", value = search, onValueChange = { search = it },
        )
        PlayerRows(direct, search, onRemove = { p ->
          onAction { apiDelete("/api/pool-players?poolId=${pool.id}&playerId=${p.id}", SuccessResponse.serializer()) }
        })
      }
    }

    if (detail.children.isNotEmpty()) {
      Div(attrs = Modifier.poolPanel().toAttrs()) {
        SpanText("Included pools", Modifier.dmStamp())
        detail.children.forEach { child ->
          ChildPoolRow(child, onRemove = {
            onAction {
              apiDelete(
                "/api/pool-children?parentPoolId=${pool.id}&childPoolId=${child.id}",
                SuccessResponse.serializer(),
              )
            }
          })
        }
      }
    }
  }
}

/** Other entries preserve the full free-form label; sports entries split optional position/team. */
internal fun parsePlayerEntry(raw: String, sport: Sport?): NewPlayerEntry? {
  if (sport == Sport.OTHER) {
    return raw.trim().takeIf { it.isNotBlank() }?.let { NewPlayerEntry(name = it, sport = sport) }
  }
  val parts = raw.split(",").map { it.trim() }
  val name = parts.getOrNull(0)?.takeIf { it.isNotBlank() } ?: return null
  return NewPlayerEntry(
    name = name,
    sport = sport,
    position = parts.getOrNull(1)?.takeIf { it.isNotBlank() },
    team = parts.getOrNull(2)?.takeIf { it.isNotBlank() },
  )
}
